import { InfoIcon } from '@carboncopyco/icons';
import { MessageBox } from '@carboncopyco/ui';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { MonacoEditor } from '../../CodeEditor';
import { useElementSize } from '../../hooks';
import { CodeInput } from '../CodeInput';
import { ErrorMessage, FormField } from '../FormField';
import { getError } from '../errors';

const fieldName = 'customModifier';

export interface CustomModifierFieldProps {
  onError?: (messages: string[]) => void;
  displayInfoMessage?: boolean;
}

export const CustomModifierFieldInfoMessage = () => (
  <MessageBox
    border
    leftIcon={<InfoIcon />}
    label="End-customers will not be able to edit rules with raw modifiers"
  />
);

export const CustomModifierField = ({
  onError,
  displayInfoMessage,
}: CustomModifierFieldProps) => {
  const {
    register,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext();
  const [containerRef, { height }] = useElementSize();
  const isFormatted = useRef(false);

  const editorRef = useRef<MonacoEditor>();
  const [errorMessage, setErrorMessage] = useState(null);
  const modifierValue = watch(fieldName);
  const editorHeight = displayInfoMessage ? height - 15 : height;

  const formatJson = () => {
    editorRef.current
      ?.getAction('editor.action.formatDocument')
      ?.run()
      .then(() => {
        editorRef.current?.revealLine(1);
        isFormatted.current = true;
      });
  };

  useEffect(() => {
    register(fieldName);
  }, []);

  return (
    <FormField
      name={fieldName}
      className="flex-1"
    >
      <div className="flex flex-col flex-1">
        <div
          className="flex flex-col flex-1"
          ref={containerRef}
        >
          <CodeInput
            onMount={(editor) => {
              editorRef.current = editor;
              formatJson();
              editorRef.current?.focus();
            }}
            onChange={(res) => {
              setValue(fieldName, res, {
                shouldDirty: isFormatted.current,
              });
              trigger(fieldName);
            }}
            onError={(errors) => {
              const errorMessages = errors.map(({ message }) => message);
              setErrorMessage(errorMessages.length ? errorMessages[0] : null);
              onError?.(errorMessages);
            }}
            value={modifierValue}
            language="json"
            height={editorHeight}
            state={errors[fieldName] || errorMessage ? 'error' : 'normal'}
          />
          <ErrorMessage
            errors={
              errorMessage
                ? {
                    [fieldName]: {
                      message: errorMessage,
                      type: 'validation',
                    },
                  }
                : errors?.[fieldName]
                  ? {
                      [fieldName]: getError(errors[fieldName]),
                    }
                  : {}
            }
          />
        </div>

        {displayInfoMessage && (
          <div className="flex">
            <CustomModifierFieldInfoMessage />{' '}
          </div>
        )}
      </div>
    </FormField>
  );
};
